// Import libraries
import QRCode from 'qrcode';
import jsQR from 'jsqr';

// Configuration
let CHUNK_SIZE = 1000; // bytes per QR code (adjustable based on QR capacity)
let QR_CYCLE_INTERVAL = 200; // milliseconds between QR code changes (default 5 codes/sec)
let SCAN_INTERVAL = 100; // milliseconds between camera scans (default 10 scans/sec)
let DISPLAY_SIZE = 400; // pixels for QR code and video display

// Helper function to calculate optimal QR code size for crisp rendering
async function getOptimalQRSize(data, targetSize) {
    try {
        // Create QR code to get module count
        const qr = QRCode.create(data, { errorCorrectionLevel: 'L' });
        const moduleCount = qr.modules.size;
        const margin = 2; // 1 module margin on each side
        const totalModules = moduleCount + (margin * 2);
        
        // Find the largest multiple of totalModules that fits within targetSize
        const pixelsPerModule = Math.floor(targetSize / totalModules);
        const optimalSize = pixelsPerModule * totalModules;
        
        return optimalSize;
    } catch (err) {
        console.error('Error calculating QR size:', err);
        return targetSize; // Fallback to target size
    }
}

// Global state
let currentMode = 'sender';
let fileData = null;
let fileChunks = [];
let fileName = '';
let fileType = '';
let totalChunks = 0;
let currentChunkIndex = 0;
let confirmedChunks = new Set();
let transmissionInterval = null;
let scanningInterval = null;
let videoStream = null;
let senderVideoStream = null;
let senderScanningInterval = null;
let scanTimestamps = []; // Track successful scan timestamps for rate calculation
let scanRateInterval = null;

// Receiver state
let receivedChunks = new Map();
let expectedChunks = 0;
let receivedFileName = '';
let receivedFileType = '';
let statusQRInterval = null;
let currentFacingMode = 'user'; // 'environment' for rear, 'user' for front
let availableCameras = [];
let receiveScanTimestamps = []; // Track successful scan timestamps for receiver rate calculation
let receiveScanRateInterval = null;

// DOM Elements
const senderBtn = document.getElementById('senderBtn');
const receiverBtn = document.getElementById('receiverBtn');
const senderMode = document.getElementById('senderMode');
const receiverMode = document.getElementById('receiverMode');
const fileInput = document.getElementById('fileInput');
const fileInfo = document.getElementById('fileInfo');
const startSendBtn = document.getElementById('startSendBtn');
const stopSendBtn = document.getElementById('stopSendBtn');
const senderQR = document.getElementById('senderQR');
const senderStatus = document.getElementById('senderStatus');
const senderVideo = document.getElementById('senderVideo');
const senderCanvas = document.getElementById('senderCanvas');
const feedbackStatus = document.getElementById('feedbackStatus');

const startReceiveBtn = document.getElementById('startReceiveBtn');
const stopReceiveBtn = document.getElementById('stopReceiveBtn');
const switchCameraBtn = document.getElementById('switchCameraBtn');
const downloadBtn = document.getElementById('downloadBtn');
const video = document.getElementById('video');
const canvas = document.getElementById('canvas');
const receiverStatusQR = document.getElementById('receiverStatusQR');
const receiverStatus = document.getElementById('receiverStatus');

// Mode switching
senderBtn.addEventListener('click', () => switchMode('sender'));
receiverBtn.addEventListener('click', () => switchMode('receiver'));

function switchMode(mode) {
    currentMode = mode;
    
    if (mode === 'sender') {
        senderBtn.classList.add('active');
        receiverBtn.classList.remove('active');
        senderMode.classList.add('active');
        receiverMode.classList.remove('active');
        stopReceiving();
    } else {
        receiverBtn.classList.add('active');
        senderBtn.classList.remove('active');
        receiverMode.classList.add('active');
        senderMode.classList.remove('active');
        stopSending();
    }
}

// File selection
fileInput.addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    fileName = file.name;
    fileType = file.type || 'application/octet-stream';
    
    const reader = new FileReader();
    reader.onload = async (event) => {
        fileData = event.target.result;
        await prepareFileChunks();
        updateFileInfo();
        startSendBtn.disabled = false;
    };
    reader.readAsArrayBuffer(file);
});

async function prepareFileChunks() {
    const uint8Array = new Uint8Array(fileData);
    fileChunks = [];
    
    for (let i = 0; i < uint8Array.length; i += CHUNK_SIZE) {
        const chunk = uint8Array.slice(i, i + CHUNK_SIZE);
        const base64Chunk = btoa(String.fromCharCode(...chunk));
        fileChunks.push(base64Chunk);
    }
    
    totalChunks = fileChunks.length;
    document.getElementById('totalChunks').textContent = totalChunks;
}

function updateFileInfo() {
    const sizeKB = (fileData.byteLength / 1024).toFixed(2);
    fileInfo.innerHTML = `
        <strong>${fileName}</strong><br>
        Size: ${sizeKB} KB<br>
        Chunks: ${totalChunks}
    `;
}

// Sender functionality
startSendBtn.addEventListener('click', startSending);
stopSendBtn.addEventListener('click', stopSending);

async function startSending() {
    if (!fileData || fileChunks.length === 0) return;
    
    startSendBtn.disabled = true;
    stopSendBtn.disabled = false;
    confirmedChunks.clear();
    currentChunkIndex = 0;
    
    senderStatus.textContent = 'Transmitting...';
    
    // Start scanning for receiver feedback
    startScanningReceiverFeedback();
    
    // Start transmission cycle
    transmissionInterval = setInterval(sendNextChunk, QR_CYCLE_INTERVAL);
}

function stopSending() {
    if (transmissionInterval) {
        clearInterval(transmissionInterval);
        transmissionInterval = null;
    }
    if (senderScanningInterval) {
        clearInterval(senderScanningInterval);
        senderScanningInterval = null;
    }
    if (scanRateInterval) {
        clearInterval(scanRateInterval);
        scanRateInterval = null;
    }
    
    if (senderVideoStream) {
        senderVideoStream.getTracks().forEach(track => track.stop());
        senderVideoStream = null;
    }
    
    startSendBtn.disabled = false;
    stopSendBtn.disabled = true;
    senderStatus.textContent = 'Transmission stopped';
    feedbackStatus.textContent = 'Waiting for receiver feedback...';
    
    // Reset scan rate display
    document.getElementById('scanRate').textContent = '0';
}

async function sendNextChunk() {
    if (confirmedChunks.size === totalChunks) {
        senderStatus.textContent = 'Transfer complete!';
        stopSending();
        return;
    }
    
    // Find next unconfirmed chunk
    while (confirmedChunks.has(currentChunkIndex) && currentChunkIndex < totalChunks) {
        currentChunkIndex++;
    }
    
    if (currentChunkIndex >= totalChunks) {
        currentChunkIndex = 0;
        return;
    }
    
    const chunkData = {
        type: 'chunk',
        fileName: fileName,
        fileType: fileType,
        chunkIndex: currentChunkIndex,
        totalChunks: totalChunks,
        data: fileChunks[currentChunkIndex]
    };
    
    const qrData = JSON.stringify(chunkData);
    
    // Calculate optimal size for crisp rendering
    const qrSize = await getOptimalQRSize(qrData, DISPLAY_SIZE);
    
    // Set display size
    senderQR.style.width = qrSize + 'px';
    senderQR.style.height = qrSize + 'px';
    
    await QRCode.toCanvas(senderQR, qrData, {
        width: qrSize,
        margin: 1,
        errorCorrectionLevel: 'L'
    });
    
    document.getElementById('currentChunk').textContent = currentChunkIndex + 1;
    document.getElementById('confirmedChunks').textContent = confirmedChunks.size;
    
    const progress = ((confirmedChunks.size / totalChunks) * 100).toFixed(1);
    document.getElementById('progress').textContent = progress + '%';
    
    currentChunkIndex++;
}

async function startScanningReceiverFeedback() {
    try {
        senderVideoStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user' }
        });
        senderVideo.srcObject = senderVideoStream;
        
        feedbackStatus.textContent = 'Scanning for receiver feedback...';
        
        // Reset scan tracking
        scanTimestamps = [];
        
        // Start scanning receiver status QR codes
        senderScanningInterval = setInterval(scanReceiverStatusQR, SCAN_INTERVAL);
        
        // Start scan rate calculation
        scanRateInterval = setInterval(updateScanRate, 1000);
    } catch (err) {
        feedbackStatus.textContent = 'Camera access denied: ' + err.message;
        console.error('Sender camera error:', err);
    }
}

function scanReceiverStatusQR() {
    if (senderVideo.readyState === senderVideo.HAVE_ENOUGH_DATA) {
        senderCanvas.width = senderVideo.videoWidth;
        senderCanvas.height = senderVideo.videoHeight;
        
        const ctx = senderCanvas.getContext('2d');
        ctx.drawImage(senderVideo, 0, 0, senderCanvas.width, senderCanvas.height);
        
        const imageData = ctx.getImageData(0, 0, senderCanvas.width, senderCanvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
            processReceiverStatus(code.data);
        }
    }
}

function processReceiverStatus(data) {
    try {
        const parsedData = JSON.parse(data);
        
        if (parsedData.type === 'status') {
            const { receivedChunks } = parsedData;
            
            // Track successful scan
            scanTimestamps.push(Date.now());
            
            // Update confirmed chunks based on receiver feedback
            receivedChunks.forEach(chunkIndex => {
                confirmedChunks.add(chunkIndex);
            });
            
            document.getElementById('confirmedChunks').textContent = confirmedChunks.size;
            
            const progress = ((confirmedChunks.size / totalChunks) * 100).toFixed(1);
            document.getElementById('progress').textContent = progress + '%';
            
            feedbackStatus.textContent = `Receiver has ${receivedChunks.length}/${totalChunks} chunks`;
        }
    } catch (e) {
        // Invalid QR code data, ignore
    }
}

function updateScanRate() {
    const now = Date.now();
    const fiveSecondsAgo = now - 5000;
    
    // Remove timestamps older than 5 seconds
    scanTimestamps = scanTimestamps.filter(timestamp => timestamp > fiveSecondsAgo);
    
    // Calculate scans per second over the last 5 seconds
    const scansInWindow = scanTimestamps.length;
    const rate = (scansInWindow / 5).toFixed(1);
    
    document.getElementById('scanRate').textContent = rate;
}

// Receiver functionality
startReceiveBtn.addEventListener('click', startReceiving);
stopReceiveBtn.addEventListener('click', stopReceiving);
switchCameraBtn.addEventListener('click', switchCamera);
downloadBtn.addEventListener('click', downloadFile);

async function startReceiving() {
    try {
        videoStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: currentFacingMode }
        });
        video.srcObject = videoStream;
        
        startReceiveBtn.disabled = true;
        stopReceiveBtn.disabled = false;
        switchCameraBtn.disabled = false;
        
        receivedChunks.clear();
        expectedChunks = 0;
        
        receiverStatus.textContent = 'Scanning for QR codes...';
        
        // Reset scan tracking
        receiveScanTimestamps = [];
        
        // Start scanning
        scanningInterval = setInterval(scanQRCode, SCAN_INTERVAL);
        
        // Start broadcasting status
        statusQRInterval = setInterval(broadcastStatus, 500);
        
        // Start scan rate calculation
        receiveScanRateInterval = setInterval(updateReceiveScanRate, 1000);
        
    } catch (err) {
        receiverStatus.textContent = 'Camera access denied: ' + err.message;
        console.error('Camera error:', err);
    }
}

async function switchCamera() {
    // Toggle between front and rear camera
    currentFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment';
    
    // Stop current stream
    if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
    }
    
    // Start new stream with switched camera
    try {
        videoStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: currentFacingMode }
        });
        video.srcObject = videoStream;
        
        receiverStatus.textContent = `Switched to ${currentFacingMode === 'environment' ? 'rear' : 'front'} camera`;
    } catch (err) {
        receiverStatus.textContent = 'Failed to switch camera: ' + err.message;
        console.error('Camera switch error:', err);
    }
}

function stopReceiving() {
    if (scanningInterval) {
        clearInterval(scanningInterval);
        scanningInterval = null;
    }
    
    if (statusQRInterval) {
        clearInterval(statusQRInterval);
        statusQRInterval = null;
    }
    
    if (receiveScanRateInterval) {
        clearInterval(receiveScanRateInterval);
        receiveScanRateInterval = null;
    }
    
    if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
        videoStream = null;
    }
    
    startReceiveBtn.disabled = false;
    stopReceiveBtn.disabled = true;
    switchCameraBtn.disabled = true;
    receiverStatus.textContent = 'Scanning stopped';
    
    // Reset scan rate display
    document.getElementById('receiveScanRate').textContent = '0';
}

function scanQRCode() {
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
            processQRCode(code.data);
        }
    }
}

function processQRCode(data) {
    try {
        const parsedData = JSON.parse(data);
        
        if (parsedData.type === 'chunk') {
            const { chunkIndex, totalChunks, data, fileName, fileType } = parsedData;
            
            // Track successful scan
            receiveScanTimestamps.push(Date.now());
            
            if (expectedChunks === 0) {
                expectedChunks = totalChunks;
                receivedFileName = fileName;
                receivedFileType = fileType;
                document.getElementById('expectedChunks').textContent = expectedChunks;
            }
            
            if (!receivedChunks.has(chunkIndex)) {
                receivedChunks.set(chunkIndex, data);
                
                document.getElementById('receivedChunks').textContent = receivedChunks.size;
                const progress = ((receivedChunks.size / expectedChunks) * 100).toFixed(1);
                document.getElementById('receiveProgress').textContent = progress + '%';
                
                receiverStatus.textContent = `Received chunk ${chunkIndex + 1}/${expectedChunks}`;
                
                updateReceivedFileInfo();
                
                if (receivedChunks.size === expectedChunks) {
                    receiverStatus.textContent = 'Transfer complete! Ready to download.';
                    downloadBtn.disabled = false;
                }
            }
        }
    } catch (e) {
        // Invalid QR code data, ignore
    }
}

function updateReceiveScanRate() {
    const now = Date.now();
    const fiveSecondsAgo = now - 5000;
    
    // Remove timestamps older than 5 seconds
    receiveScanTimestamps = receiveScanTimestamps.filter(timestamp => timestamp > fiveSecondsAgo);
    
    // Calculate scans per second over the last 5 seconds
    const scansInWindow = receiveScanTimestamps.length;
    const rate = (scansInWindow / 5).toFixed(1);
    
    document.getElementById('receiveScanRate').textContent = rate;
}

async function broadcastStatus() {
    const receivedIndices = Array.from(receivedChunks.keys());
    const statusData = {
        type: 'status',
        receivedChunks: receivedIndices,
        totalReceived: receivedChunks.size,
        expectedTotal: expectedChunks
    };
    
    const qrData = JSON.stringify(statusData);
    const targetSize = parseInt(receiverStatusQR.style.width) || 400;
    
    // Calculate optimal size for crisp rendering
    const qrSize = await getOptimalQRSize(qrData, targetSize);
    
    // Set display size
    receiverStatusQR.style.width = qrSize + 'px';
    receiverStatusQR.style.height = qrSize + 'px';
    
    await QRCode.toCanvas(receiverStatusQR, qrData, {
        width: qrSize,
        margin: 1,
        errorCorrectionLevel: 'L'
    });
}

function updateReceivedFileInfo() {
    document.getElementById('receivedFileInfo').innerHTML = `
        <strong>File:</strong> ${receivedFileName || 'Unknown'}<br>
        <strong>Type:</strong> ${receivedFileType || 'Unknown'}<br>
        <strong>Progress:</strong> ${receivedChunks.size}/${expectedChunks} chunks
    `;
}

function downloadFile() {
    if (receivedChunks.size !== expectedChunks) {
        alert('File transfer not complete!');
        return;
    }
    
    // Reconstruct file from chunks
    const chunks = [];
    for (let i = 0; i < expectedChunks; i++) {
        const base64Data = receivedChunks.get(i);
        if (!base64Data) {
            alert(`Missing chunk ${i}!`);
            return;
        }
        
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let j = 0; j < binaryString.length; j++) {
            bytes[j] = binaryString.charCodeAt(j);
        }
        chunks.push(bytes);
    }
    
    // Combine all chunks
    const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const combinedArray = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
        combinedArray.set(chunk, offset);
        offset += chunk.length;
    }
    
    // Create blob and download
    const blob = new Blob([combinedArray], { type: receivedFileType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = receivedFileName || 'downloaded-file';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    receiverStatus.textContent = 'File downloaded successfully!';
}

// Speed control sliders
const speedSlider = document.getElementById('speedSlider');
const speedValue = document.getElementById('speedValue');

speedSlider.addEventListener('input', (e) => {
    const codesPerSecond = parseInt(e.target.value);
    speedValue.textContent = codesPerSecond;
    QR_CYCLE_INTERVAL = 1000 / codesPerSecond;
    
    // If transmission is active, restart with new interval
    if (transmissionInterval) {
        clearInterval(transmissionInterval);
        transmissionInterval = setInterval(sendNextChunk, QR_CYCLE_INTERVAL);
    }
});

// Receiver scan speed control
const scanSpeedSlider = document.getElementById('scanSpeedSlider');
const scanSpeedValue = document.getElementById('scanSpeedValue');

scanSpeedSlider.addEventListener('input', (e) => {
    const scansPerSecond = parseInt(e.target.value);
    scanSpeedValue.textContent = scansPerSecond;
    SCAN_INTERVAL = 1000 / scansPerSecond;
    
    // If scanning is active, restart with new interval
    if (scanningInterval) {
        clearInterval(scanningInterval);
        scanningInterval = setInterval(scanQRCode, SCAN_INTERVAL);
    }
});

// Display size control
const displaySizeSlider = document.getElementById('displaySizeSlider');
const displaySizeValue = document.getElementById('displaySizeValue');

displaySizeSlider.addEventListener('input', (e) => {
    const size = parseInt(e.target.value);
    displaySizeValue.textContent = size;
    DISPLAY_SIZE = size;
    
    // Update sender display sizes
    senderQR.style.width = size + 'px';
    senderQR.style.height = size + 'px';
    senderVideo.style.width = size + 'px';
    senderVideo.style.height = size + 'px';
});

// Chunk size control
const chunkSizeSlider = document.getElementById('chunkSizeSlider');
const chunkSizeValue = document.getElementById('chunkSizeValue');

chunkSizeSlider.addEventListener('input', async (e) => {
    const size = parseInt(e.target.value);
    chunkSizeValue.textContent = size;
    CHUNK_SIZE = size;
    
    // If file is loaded, re-chunk it
    if (fileData) {
        await prepareFileChunks();
        updateFileInfo();
    }
});

// Receiver display size control
const receiverDisplaySizeSlider = document.getElementById('receiverDisplaySizeSlider');
const receiverDisplaySizeValue = document.getElementById('receiverDisplaySizeValue');

receiverDisplaySizeSlider.addEventListener('input', (e) => {
    const size = parseInt(e.target.value);
    receiverDisplaySizeValue.textContent = size;
    
    // Update receiver display sizes
    video.style.width = size + 'px';
    video.style.height = size + 'px';
    receiverStatusQR.style.width = size + 'px';
    receiverStatusQR.style.height = size + 'px';
});

// Initialize display sizes
senderQR.style.width = '400px';
senderQR.style.height = '400px';
senderVideo.style.width = '400px';
senderVideo.style.height = '400px';
video.style.width = '400px';
video.style.height = '400px';
receiverStatusQR.style.width = '400px';
receiverStatusQR.style.height = '400px';

// Initialize
senderStatus.textContent = 'Select a file to begin';
receiverStatus.textContent = 'Click "Start Receiving" to begin';
